import type { SearchParamCodec } from '@repo/ui/lib/hooks/use-search-params-form';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
  valueParam,
} from '@/lib/helpers/list-api-params';
import {
  ACTIVE_STATUS_DEFAULT_TAB,
  ACTIVE_STATUS_TABS,
} from '@/lib/helpers/list-tabs';
import type { FetchMcpClientsParams } from '@/api/mcp-clients';
import type { McpClientsSearch } from '@/routes/-shared/mcpClientsSearchSchema';

export const MCP_CLIENTS_DEFAULT_TAB = ACTIVE_STATUS_DEFAULT_TAB;

/** Tab della lista: lo stato attivo, comune alle liste con `isActive`. */
export const MCP_CLIENTS_TABS = ACTIVE_STATUS_TABS;

export const VERIFICATION_FILTER = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
} as const;

export type VerificationFilterValue =
  (typeof VERIFICATION_FILTER)[keyof typeof VERIFICATION_FILTER];

export const VERIFICATION_FILTER_OPTIONS: readonly {
  id: VerificationFilterValue;
  labelKey: string;
}[] = [
  { id: VERIFICATION_FILTER.VERIFIED, labelKey: 'commons.verified' },
  { id: VERIFICATION_FILTER.UNVERIFIED, labelKey: 'commons.unverified' },
];

/**
 * Filtro «Verifica» del popover: nell'URL resta il booleano `verified` che
 * l'API conosce, nel combobox (id stringa) diventa `verified`/`unverified`.
 * Converge: `decode(encode(v)) = v`, e l'assenza vale «nessun filtro».
 */
export const verificationFilterParam: SearchParamCodec<
  'verified',
  VerificationFilterValue
> = {
  keys: ['verified'],
  decode: ({ verified }) => {
    if (verified === true) return VERIFICATION_FILTER.VERIFIED;
    if (verified === false) return VERIFICATION_FILTER.UNVERIFIED;
    return undefined;
  },
  encode: (value) => {
    if (value === VERIFICATION_FILTER.VERIFIED) return { verified: true };
    if (value === VERIFICATION_FILTER.UNVERIFIED) return { verified: false };
    return { verified: undefined };
  },
};

type McpClientsApiParams = Omit<FetchMcpClientsParams, 'skip' | 'take'>;

/** Campi di `GET backoffice/mcp-clients`; lo stato attivo arriva dal tab. */
const MCP_CLIENTS_API_PARAMS: ApiParamReaders<
  McpClientsSearch,
  McpClientsApiParams
> = {
  ...SEARCH_AND_SORT_PARAMS,
  verified: valueParam,
};

export function buildMcpClientsApiParams(
  search: McpClientsSearch,
  isActive: boolean | undefined
): McpClientsApiParams {
  return { ...pickApiParams(search, MCP_CLIENTS_API_PARAMS), isActive };
}
